import React from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { MOBILE_TYPOGRAPHY } from '@/constants/residents/mobile/mobileTypography';

interface DetailHeaderCardProps {
  /** Title text */
  title: string;
  /** Description text */
  description: string;
  /** Icon component to render in avatar */
  icon?: React.ReactNode;
  /** Image URL for avatar (takes precedence over icon) */
  imageUri?: string;
  /** Background color for card (default: #242424) */
  cardBackground?: string;
  /** Background color for avatar when using icon (default: rgba(238, 241, 244, 0.1)) */
  avatarBackground?: string;
  /** Accent border color (default: #F55560) */
  accentColor?: string;
  /** Title text color (default: #EEF1F4) */
  titleColor?: string;
  /** Description text color (default: #EEF1F4) */
  descriptionColor?: string;
  /** Custom container style */
  style?: StyleProp<ViewStyle>;
  /** Additional content to render below title/description */
  children?: React.ReactNode;
}

/**
 * DetailHeaderCard
 * 
 * Reusable header card component for detail screens.
 * Features a pink accent border on the left, avatar/icon, title, and description.
 * Matches Figma design for resource detail and contact office screens.
 * 
 * @example
 * // With icon
 * <DetailHeaderCard
 *   title="Move-in info"
 *   description="Full description lorem ipsum..."
 *   icon={<ResourceIcon iconKey="entrance" size={24} color="#EEF1F4" />}
 * />
 * 
 * @example
 * // With image
 * <DetailHeaderCard
 *   title="Contact the office"
 *   description="Property Name"
 *   imageUri={property.photoUrl}
 * />
 */
export const DetailHeaderCard: React.FC<DetailHeaderCardProps> = ({
  title,
  description,
  icon,
  imageUri,
  cardBackground = '#242424',
  avatarBackground = 'rgba(238, 241, 244, 0.1)',
  accentColor = '#F55560',
  titleColor = '#EEF1F4',
  descriptionColor = '#EEF1F4',
  style,
  children,
}) => {
  return (
    <View style={[styles.card, { backgroundColor: cardBackground }, style]}>
      {/* Pink accent border */}
      <View style={[styles.accentBorder, { backgroundColor: accentColor }]} />
      
      {/* Content */}
      <View style={styles.content}>
        {/* Avatar */}
        {imageUri ? (
          <Image
            source={{ uri: imageUri }}
            style={styles.image}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.avatar, { backgroundColor: avatarBackground }]}>
            {icon}
          </View>
        )}

        {/* Title and Description */}
        <View style={styles.textContainer}>
          <Text style={[styles.title, { color: titleColor }]}>{title}</Text>
          <Text style={[styles.description, { color: descriptionColor }]}>
            {description}
          </Text>
          {children}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    overflow: 'hidden',
    flexDirection: 'row',
  },
  accentBorder: {
    width: 2,
  },
  content: {
    flex: 1,
    padding: 20,
    flexDirection: 'row',
    gap: 16,
  },
  avatar: {
    width: 68,
    height: 68,
    borderRadius: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: 68,
    height: 68,
    borderRadius: 34,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  title: {
    ...MOBILE_TYPOGRAPHY.title,
  },
  description: {
    ...MOBILE_TYPOGRAPHY.body,
  },
});

export default DetailHeaderCard;
